/**
 * Container Node Strategies
 * 
 * Modular strategies for generating container nodes based on view mode.
 * Each strategy knows how to generate containers and what CSS layout to apply.
 */

import type { GraphNode, ContainerStrategy } from './types.js';
import { nanoid } from 'nanoid';

/**
 * Calendar container strategy
 * Generates day containers (7 for week, 28-31 for month)
 */
export class CalendarContainerStrategy implements ContainerStrategy {
  generateContainers(count: number, config: Record<string, unknown>): GraphNode[] {
    const containers: GraphNode[] = [];
    const startDate = config.startDate as Date || new Date();
    
    for (let i = 0; i < count; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      
      containers.push({
        id: `container-${nanoid()}`,
        type: 'container',
        data: {
          date: date.toISOString(),
          dayOfWeek: date.getDay(),
          dayOfMonth: date.getDate()
        },
        metadata: {
          label: date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })
        }
      });
    }
    
    return containers;
  }

  getLayoutCSS(): string {
    return `
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    `;
  }
}

/**
 * Grid container strategy
 * Generates N×M grid of cell containers
 */
export class GridContainerStrategy implements ContainerStrategy {
  generateContainers(count: number, config: Record<string, unknown>): GraphNode[] {
    const containers: GraphNode[] = [];
    const columns = (config.columns as number) || Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / columns);
    
    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / columns);
      const col = i % columns;
      
      containers.push({
        id: `container-${nanoid()}`,
        type: 'container',
        data: {
          index: i,
          row,
          col
        },
        metadata: {
          label: `${row},${col}`
        }
      });
    }
    
    return containers;
  }

  getLayoutCSS(): string {
    return `
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 8px;
    `;
  }
}

/**
 * Carousel container strategy
 * Generates linear sequence of slide containers
 */
export class CarouselContainerStrategy implements ContainerStrategy {
  generateContainers(count: number, config: Record<string, unknown>): GraphNode[] {
    const containers: GraphNode[] = [];
    
    for (let i = 0; i < count; i++) {
      containers.push({
        id: `container-${nanoid()}`,
        type: 'container',
        data: {
          index: i,
          slide: i
        },
        metadata: {
          label: `Slide ${i + 1}`
        }
      });
    }
    
    return containers;
  }

  getLayoutCSS(): string {
    return `
      display: flex;
      flex-direction: row;
      overflow-x: auto;
      gap: 8px;
      scroll-snap-type: x mandatory;
    `;
  }
}

/**
 * Stack container strategy (Flashcard/Tinder)
 * Generates z-ordered card containers
 */
export class StackContainerStrategy implements ContainerStrategy {
  generateContainers(count: number, config: Record<string, unknown>): GraphNode[] {
    const containers: GraphNode[] = [];
    
    for (let i = 0; i < count; i++) {
      containers.push({
        id: `container-${nanoid()}`,
        type: 'container',
        data: {
          index: i,
          zIndex: count - i // Top card has highest z-index
        },
        metadata: {
          label: `Card ${i + 1}`
        }
      });
    }
    
    return containers;
  }

  getLayoutCSS(): string {
    return `
      position: relative;
      width: 100%;
      height: 100%;
    `;
  }
}

/**
 * No container strategy (for list view)
 * Returns empty array - no containers needed
 */
export class NoContainerStrategy implements ContainerStrategy {
  generateContainers(count: number, config: Record<string, unknown>): GraphNode[] {
    return [];
  }

  getLayoutCSS(): string {
    return `
      display: flex;
      flex-direction: column;
      gap: 8px;
    `;
  }
}

